import { 
  Title, 
  Text, 
  Button, 
  Stack,
  Box,
  Image,
  Container,
  Group,
  Anchor,
  Notification,
  Flex,
  MantineProvider,
  rem,
  Paper,
  Divider
} from '@mantine/core';
import { useState, useEffect } from 'react';
import { FormData } from '@/types';
import { CSSProperties } from 'react';

// Force clean build - no Three.js dependencies needed

interface LandingProps {
  formData: FormData;
  onFormChange: (data: Partial<FormData>) => void;
  onComplete: () => void;
}

const Landing = ({ onComplete }: LandingProps) => {
  const [showCookieNotice, setShowCookieNotice] = useState(false);
  const [hoverButton, setHoverButton] = useState(false);
  
  useEffect(() => {
    // Check if cookie consent was previously given
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setShowCookieNotice(true);
    }
  }, []);
  
  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'true');
    setShowCookieNotice(false);
  };
  
  const rejectCookies = () => {
    localStorage.setItem('cookieConsent', 'false');
    setShowCookieNotice(false);
  };
  
  return (
    <Box
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'white',
        position: 'relative',
        overflow: 'hidden', // Prevent scrolling
      }}
    >
      {/* Background graphic elements */}
      <Box
        style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          top: '-20%',
          right: '-10%',
          background: 'radial-gradient(circle, rgba(10, 102, 194, 0.15) 0%, rgba(10, 102, 194, 0.05) 50%, transparent 70%)',
          borderRadius: '50%',
          opacity: 0.5,
          zIndex: 0,
        }}
      />
      <Box 
        style={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          bottom: '-10%',
          left: '-15%',
          background: 'radial-gradient(circle, rgba(10, 102, 194, 0.15) 0%, rgba(10, 102, 194, 0.05) 50%, transparent 70%)',
          borderRadius: '50%',
          opacity: 0.5,
          zIndex: 0,
        }}
      />
      
      {/* Header - Updated to match RapidLab clean style */}
      <Box
        component="header"
        style={{
          height: 80, 
          background: 'white', 
          display: 'flex',
          alignItems: 'center',
          position: 'fixed',
          top: 0,
          left: 0, 
          right: 0,
          zIndex: 100,
          borderBottom: '1px solid rgba(10, 102, 194, 0.1)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.03)',
          width: '100%'
        }}
      >
        <Container size="lg" style={{ 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0 1rem',
          width: '100%',
          maxWidth: '1200px'
        }}>
          <Flex align="center" gap={rem(8)} style={{ marginRight: 'auto' }}>
            <Box 
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => window.location.href = "/"}
            >
              {/* SVG Lightning bolt icon */}
              <Box style={{ 
                marginRight: rem(8), 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center',
                order: 1
              }}>
                <svg 
                  width="30" 
                  height="30" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ color: '#0A66C2' }}
                >
                  <path 
                    d="M13 10V3L4 14H11V21L20 10H13Z" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
              <Text style={{ 
                color: '#0A66C2', 
                fontWeight: 700, 
                fontSize: '1rem',
                letterSpacing: '-0.01em',
                order: 2,
                fontFamily: '"Bio Sans", sans-serif'
              }}>
                RAPIDLAB
              </Text>
            </Box>
          </Flex>
          
          <Flex gap={rem(16)} justify="flex-end" style={{ marginLeft: 'auto' }}>
            <Anchor 
              href="#/about"
              style={{ 
                color: '#0A66C2',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '1rem',
                fontFamily: '"Bio Sans", sans-serif'
              }}
              styles={{
                root: {
                  '&:hover': {
                    color: '#084785',
                  }
                }
              }}
            >
              About
            </Anchor>
            <Anchor 
              href="https://calendly.com/mat-rapid-lab/30min"
              target="_blank"
              style={{ 
                color: '#0A66C2',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '1rem',
                fontFamily: '"Bio Sans", sans-serif'
              }}
              styles={{
                root: {
                  '&:hover': {
                    color: '#084785',
                  }
                }
              }}
            >
              Contact
            </Anchor>
          </Flex>
        </Container>
      </Box>

      {/* Main Content */}
      <Box 
        style={{ 
          flex: '1',
          display: 'flex',
          alignItems: 'center', // Back to center alignment
          overflow: 'hidden', // Prevent scrolling
          position: 'relative', // For positioning keylines
          marginTop: 80, // Add margin to account for fixed header
        }}
      >
        {/* Left keyline - hide on mobile */}
        <Box
          style={{
            position: 'absolute',
            left: 'calc((100% - 1200px) / 2)', // Align with container max-width
            top: '0',
            bottom: '0',
            width: '1px',
            background: 'rgba(10, 102, 194, 0.15)',
            zIndex: 1,
            display: 'none', // Hide by default (mobile)
            '@media (min-width: 768px)': {
              display: 'block', // Show on desktop
            },
          }}
        />
        
        {/* Right keyline - hide on mobile */}
        <Box
          style={{
            position: 'absolute',
            right: 'calc((100% - 1200px) / 2)', // Align with container max-width
            top: '0',
            bottom: '0',
            width: '1px',
            background: 'rgba(10, 102, 194, 0.15)',
            zIndex: 1,
            display: 'none', // Hide by default (mobile)
            '@media (min-width: 768px)': {
              display: 'block', // Show on desktop
            },
          }}
        />
        
        <Container 
          size="lg" 
          style={{ 
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: 0,
            paddingBottom: 0,
            marginTop: rem(-160), // Increased negative margin to move up more
            padding: '0 1.5rem', // Add horizontal padding for mobile
          }}
        >
          <Box style={{ 
            position: 'relative', 
            zIndex: 1, 
            width: '100%',
            paddingLeft: '0', // No left padding on mobile
            '@media (min-width: 768px)': {
              paddingLeft: '10%', // Add left padding on desktop
            },
          }}>
            <Title order={1} style={{ 
              fontSize: 'clamp(2.5rem, 8vw, 5.5rem)', // Smaller font size on mobile
              fontWeight: 1000,
              lineHeight: 1.1,
              color: '#0A66C2',
              letterSpacing: '-0.02em',
              marginBottom: rem(16),
              wordBreak: 'break-word', // Better word wrapping
              hyphens: 'auto', // Allow hyphenation
              textAlign: 'left', // Ensure left alignment on all devices
            }}>
              Write Better.<br />
              Post Smarter.
            </Title>
            
            <Text style={{
              fontSize: 'clamp(1rem, 4vw, 1.75rem)', // Smaller font size on mobile
              lineHeight: 1.4,
              color: '#0A66C2',
              fontWeight: 400,
              maxWidth: '800px',
              marginBottom: rem(32), // Less margin on mobile
              wordBreak: 'break-word', // Better word wrapping
              textAlign: 'left', // Ensure left alignment on all devices
            }}>
              Test your LinkedIn posts before putting them up.<br />
              Reach more people, improve your message.
            </Text>
            
            <Box style={{ position: 'relative', display: 'inline-block' }}>
              <Button 
                size="xl"
                onClick={onComplete}
                style={{
                  fontSize: '1.25rem',
                  padding: `${rem(12)} ${rem(32)}`,
                  paddingRight: rem(45),
                  height: 'auto',
                  minHeight: rem(56),
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #2684FF 0%, #0073E6 100%)',
                  borderRadius: rem(8),
                  border: 'none',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                }}
                styles={{
                  root: {
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 12px 28px rgba(0, 0, 0, 0.25)',
                      background: 'linear-gradient(135deg, #3393ff 0%, #2684FF 100%)',
                    }
                  }
                }}
                onMouseEnter={() => setHoverButton(true)}
                onMouseLeave={() => setHoverButton(false)}
              >
                Try It Now
                <Box
                  component="span"
                  style={{
                    position: 'absolute',
                    right: rem(20),
                    display: 'inline-block',
                    transition: 'transform 0.3s ease',
                    transform: hoverButton ? 'translateX(5px)' : 'none',
                    color: 'white',
                    fontSize: '1.25rem',
                  }}
                >
                  →
                </Box>
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      
      {/* Footer - Updated with content from RapidLab image */}
      <Box
        component="footer"
        style={{
          width: '100%', 
          borderTop: '1px solid rgba(10, 102, 194, 0.15)',
          background: '#f9f9f9',
          padding: `${rem(12)} 0`, // Balanced padding
        }}
      >
        <Container size="lg">
          <Flex direction="column" align="center" gap={rem(8)}>
            <Text style={{ 
              fontSize: '0.8rem',
              color: '#555',
            }}>
              Transforming ideas into reality through rapid prototyping.
            </Text>
            
            <Flex justify="center" gap={rem(16)}>
              <Anchor 
                href="https://test.rapid-lab.com/privacy" 
                target="_blank"
                style={{
                  color: '#0A66C2',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  padding: '0 8px',
                }}
                styles={{
                  root: {
                    '&:hover': {
                      color: '#084785',
                    }
                  }
                }}
              >
                Privacy Policy
              </Anchor>
              <Text style={{ color: '#999', fontSize: '0.75rem' }}>•</Text>
              <Anchor 
                href="https://test.rapid-lab.com/terms" 
                target="_blank"
                style={{
                  color: '#0A66C2',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  padding: '0 8px',
                }}
                styles={{
                  root: {
                    '&:hover': {
                      color: '#084785',
                    }
                  }
                }}
              >
                Terms of Service
              </Anchor>
            </Flex>
            
            <Text style={{ 
              fontSize: '0.7rem',
              color: '#999',
            }}>
              © 2025 RapidLab
            </Text>
          </Flex>
        </Container>
      </Box>
      
      {/* Cookie Notice Popup */}
      {showCookieNotice && (
        <Box
          style={{
            position: 'fixed',
            bottom: rem(20),
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'calc(100% - 40px)',
            maxWidth: '440px',
            zIndex: 1000,
          }}
        >
          <Paper
            style={{
              backgroundColor: 'white',
              color: '#0A66C2',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(10, 102, 194, 0.1)',
              padding: '1rem'
            }}
          >
            <Title order={4} style={{ color: '#0A66C2', marginBottom: rem(8) }}>We Value Your Privacy</Title>
            <Text size="sm" style={{ color: '#0A66C2', marginBottom: rem(16) }}>
              This website uses cookies to improve your experience and analyze our traffic. You can accept or decline cookies at any time.
            </Text>
            <Group style={{ justifyContent: 'flex-end', gap: rem(8) }}>
              <Button 
                size="sm" 
                style={{
                  color: '#0A66C2',
                  backgroundColor: 'transparent',
                  border: 'none'
                }}
                styles={{
                  root: {
                    '&:hover': {
                      backgroundColor: 'rgba(10, 102, 194, 0.1)',
                      color: '#084785'
                    }
                  }
                }}
                onClick={rejectCookies}
              >
                Decline
              </Button>
              <Button 
                size="sm" 
                style={{
                  backgroundColor: '#0A66C2',
                  color: 'white',
                  fontWeight: 500
                }}
                styles={{
                  root: {
                    '&:hover': {
                      backgroundColor: '#084785'
                    }
                  }
                }}
                onClick={acceptCookies}
              >
                Accept All
              </Button>
            </Group>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Landing; 